import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Handshake,
  TrendingUp,
  Shield,
  Star,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const BecomeMemberHero = ({ showApplyButton = true, compact = false }) => {
  // ————— Website-first benefits (focus on Stores) —————
  const websiteUserBenefits = [
    {
      icon: <Shield className="w-6 h-6 text-blue-600" />,
      title: "Access the Stores",
      description: "Cooperative, Member, and Merchant Stores in one app"
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-green-600" />,
      title: "Easy Ordering",
      description: "Checkout online and track deliveries in real time"
    },
    {
      icon: <Users className="w-6 h-6 text-purple-600" />,
      title: "Deals & Promos",
      description: "Exclusive vouchers and payday sales"
    },
    {
      icon: <Handshake className="w-6 h-6 text-orange-600" />,
      title: "Future Perks",
      description: "Eligible for member pricing and priority slots"
    }
  ];

  // ————— Membership benefits (short, business-system angle) —————
  const cooperativeBenefits = [
    {
      icon: <Star className="w-6 h-6 text-green-600" />,
      title: "Be Part-Owner",
      description: "Own a share and help steer a growing business system"
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-blue-600" />,
      title: "Grow With Us",
      description: "Priority access to new store features and pilots"
    },
    {
      icon: <Users className="w-6 h-6 text-purple-600" />,
      title: "Network & Support",
      description: "Connect with sellers, riders, and local partners"
    },
    {
      icon: <Handshake className="w-6 h-6 text-orange-600" />,
      title: "Rewards & Returns",
      description: "Potential patronage/earnings when declared"
    }
  ];

  // Keep a light, simple requirements list (still present but de-emphasized)
  const requirements = [
    "18 years old and above",
    "Lives/operates in Puerto Princesa",
    "Valid government ID",
    "Initial share capital",
    "Agrees to policies & bylaws"
  ];

  if (compact) {
    return (
      <div className="bg-gradient-to-br from-surface to-moss-50 border border-border rounded-lg p-6">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 break-words">
            Join the PPAC Website
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6 break-words px-2">
            Create a free account to shop our Stores. Want more? Become a member and be part of this growing business system.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center px-4 sm:px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
            >
              Create Account
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
            {showApplyButton && (
              <Link
                to="/signup"
                className="inline-flex items-center px-4 sm:px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
              >
                Start Application
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      {/* Background with subtle pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-surface via-moss-50 to-moss-100">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
        <div className="text-center">
          {/* Main Heading */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight break-words px-2">
              Shop the
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">
                {" "}PPAC Stores
              </span>
              <br />
              Online, Fast, and Easy
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed break-words px-4">
              Start with a free website account to access our Cooperative Store, Member Stores, and Merchant Stores.
              If you want a bigger role, become a member and be part of this great, growing business system.
            </p>
          </div>

          {/* Website User Signup Section */}
          <div className="mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-blue-200 max-w-5xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center justify-center break-words px-2">
                <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 mr-3 flex-shrink-0" />
                Step 1: Create Your Website Account
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 max-w-3xl mx-auto break-words px-4">
                Get instant access to our Stores, check out in minutes, and track your delivery from stall to doorstep.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
                {websiteUserBenefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="bg-blue-50/80 rounded-xl p-3 sm:p-4 border border-blue-100 hover:border-blue-200 transition-all duration-300 hover:shadow-md"
                  >
                    <div className="flex justify-center mb-3">{benefit.icon}</div>
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2 text-center break-words">
                      {benefit.title}
                    </h3>
                    <p className="text-xs text-gray-600 text-center leading-relaxed break-words">
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold text-base sm:text-lg rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 break-words"
                >
                  Create Website Account
                  <ArrowRight className="ml-2 w-5 h-5 sm:w-6 sm:h-6" />
                </Link>
              </div>
            </div>
          </div>

          {/* OR Divider */}
          <div className="mb-8">
            <div className="flex items-center justify-center">
              <div className="w-16 h-px bg-gray-300"></div>
              <span className="px-6 text-lg font-semibold text-gray-500">OR</span>
              <div className="w-16 h-px bg-gray-300"></div>
            </div>
          </div>

          {/* Membership Section (short + business-system angle) */}
          <div className="mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-green-200 max-w-5xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex items-center justify-center break-words px-2">
                <Star className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mr-3 flex-shrink-0" />
                Step 2 (Optional): Become a Member
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 max-w-3xl mx-auto break-words px-4">
                Level up your role—own a share, join a growing business system, and open the door to rewards and returns.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
                {cooperativeBenefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="bg-green-50/80 rounded-xl p-3 sm:p-4 border border-green-100 hover:border-green-200 transition-all duration-300 hover:shadow-md"
                  >
                    <div className="flex justify-center mb-3">{benefit.icon}</div>
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2 text-center break-words">
                      {benefit.title}
                    </h3>
                    <p className="text-xs text-gray-600 text-center leading-relaxed break-words">
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>

              {showApplyButton && (
                <div className="text-center">
                  <Link
                    to="/signup"
                    className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold text-base sm:text-lg rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 break-words"
                  >
                    Start Application
                    <ArrowRight className="ml-2 w-5 h-5 sm:w-6 sm:h-6" />
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Requirements Section (kept, but succinct) */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-green-100 max-w-4xl mx-auto">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center justify-center break-words px-2">
              <Star className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-500 mr-3 flex-shrink-0" />
              Membership Requirements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {requirements.map((requirement, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 text-sm break-words">{requirement}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="mt-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-green-100 max-w-4xl mx-auto">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center justify-center break-words px-2">
                <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 text-green-500 mr-3 flex-shrink-0" />
                Need Help? Contact Us
              </h3>
              <p className="text-base sm:text-lg text-gray-600 mb-6 max-w-3xl mx-auto text-center break-words px-4">
                Our membership team is here to answer your questions and guide you through the process.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {/* Phone */}
                <div className="bg-green-50/80 rounded-xl p-4 border border-green-100 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Phone</h4>
                  <p className="text-green-600 font-medium">+63 950 468 6668</p>
                </div>

                {/* Email */}
                <div className="bg-green-50/80 rounded-xl p-4 border border-green-100 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Email</h4>
                  <p className="text-green-600 font-medium">ppcagriculturalcoop@gmail.com</p>
                </div>

                {/* Office */}
                <div className="bg-green-50/80 rounded-xl p-4 border border-green-100 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Office</h4>
                  <p className="text-green-600 font-medium">Agri Trading Center, Brgy. Irawan, Puerto Princesa City, Palawan 5300</p>
                </div>

                {/* Office Hours */}
                <div className="bg-green-50/80 rounded-xl p-4 border border-green-100 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Office Hours</h4>
                  <p className="text-green-600 font-medium">6:00 AM - 6:00 PM</p>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-gray-600 text-sm break-words px-2">
                  Have specific questions about membership?
                </p>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm mb-4 break-words px-2">
              Trusted by local shoppers and partner sellers
            </p>
            <p className="text-base sm:text-lg font-medium text-gray-600 mb-4 break-words px-2">
              We see our future like this:
            </p>
            <div className="flex items-center justify-center space-x-4 sm:space-x-8 opacity-60">
              <div className="text-xl sm:text-2xl font-bold text-green-600">500+</div>
              <div className="text-gray-400">|</div>
              <div className="text-xl sm:text-2xl font-bold text-blue-600">15+</div>
              <div className="text-gray-400">|</div>
              <div className="text-xl sm:text-2xl font-bold text-purple-600">₱50M+</div>
            </div>
            <div className="flex items-center justify-center space-x-4 sm:space-x-8 text-xs sm:text-sm text-gray-500 mt-2">
              <span className="break-words">Active Shoppers</span>
              <span className="break-words">Partner Sellers</span>
              <span className="break-words">GMV Processed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>
    </div>
  );
};

export default BecomeMemberHero;
